import React, { useEffect, useRef, useState, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { REPLY_SYMBOL } from '@sv-support-sdk/utils/src/constants/chatMessages';

import css from './style.module.scss';

import Button from '../ui/Controls/Button';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { chatIdSelector, chatRoomSelector } from '../../store/selectors/chat';
import { sendMessage } from '../../store/actions/messages';
import AttachmentsButton from './AttachmentsButton';
import socket from '../../socket';
import { selectUserEmail } from '../../store/selectors/user';
import Reply from '../ui/Content/Reply';
import { replyToSupportSelector } from '../../store/selectors/messages';
import { setReplyToSupport } from '../../store/slices/messages';
import setQaAttribute from '../../helpers/setQaAttribute';
import { CHAT_MESSAGE_FORM, SEND_CHAT_MESSAGE } from '../../constants/qaAttributes';

const MessageForm = () => {
  const hideControls = false;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const userName = useAppSelector(selectUserEmail);
  const chatRoom = useAppSelector(chatRoomSelector);
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const chatId = useAppSelector(chatIdSelector);
  const replyToSupport = useAppSelector(replyToSupportSelector);

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(event.target.value);
  };

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      textareaRef.current.style.height = '0px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = scrollHeight + 'px';
    }
  }, [textareaValue]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
      send();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      dispatch(setReplyToSupport(''));
    } else {
      const socketObj = socket();
      if (socketObj) {
        // @ts-expect-error in whisper
        socketObj.private(chatRoom).whisper('clientTyping', {
          name: userName,
          chat_id: chatId,
          message: textareaValue && textareaValue.trim(),
        });
      }
    }
  };

  useEffect(() => {
    if (replyToSupport) {
      textareaRef.current?.focus();
    }
  }, [replyToSupport]);

  const send = async () => {
    const messageToSend = textareaValue.trim();
    if (!messageToSend.length) return;

    try {
      setLoading(true);

      let text = messageToSend;

      if (replyToSupport) {
        text = `${REPLY_SYMBOL} "${replyToSupport}"\n\n${text}`;
      }

      await dispatch(sendMessage(text));
      dispatch(setReplyToSupport(''));
      setTextareaValue('');
    } finally {
      setLoading(false);
    }
  };

  if (hideControls) return null;

  return (
    <div className={classNames(css.chat__controls, { [css.isDisabled]: loading })}>
      <AttachmentsButton visible={!!chatId} />
      <div className={css.chat__textareaWrap}>
        {replyToSupport && <Reply type="fromUser">{replyToSupport}</Reply>}
        <textarea
          ref={textareaRef}
          onChange={handleTextareaChange}
          className={css.chat__textarea}
          placeholder="Send us a message…"
          onKeyDown={onKeyDown}
          value={textareaValue}
          {...setQaAttribute(CHAT_MESSAGE_FORM)}
        >
          {textareaValue}
        </textarea>
      </div>
      <div className={css.chat__controlsButton}>
        <Button
          icon="24--send"
          iconSize="24"
          radius="circle"
          shape="square"
          size="small"
          variant="icon"
          onClick={send}
          {...setQaAttribute(SEND_CHAT_MESSAGE)}
        />
      </div>
    </div>
  );
};

export default MessageForm;
